import type { Dayjs, OpUnitType } from 'dayjs';

import utc from 'dayjs/plugin/utc';
import duration from 'dayjs/plugin/duration';
import timezone from 'dayjs/plugin/timezone';
import relativeTime from 'dayjs/plugin/relativeTime';

import dayjs, { dayjsSystem } from 'src/lib/dayjs';

// ----------------------------------------------------------------------

/**
 * @Docs
 * https://day.js.org/docs/en/display/format
 */

/**
 * Default timezones
 * https://day.js.org/docs/en/timezone/set-default-timezone#docsNav
 *
 */

/**
 * UTC
 * https://day.js.org/docs/en/plugin/utc
 * @install
 * import utc from 'dayjs/plugin/utc';
 * dayjs.extend(utc);
 * @usage
 * dayjs().utc().format()
 *
 */

dayjs.extend(duration);
dayjs.extend(relativeTime);
dayjs.extend(utc);
dayjs.extend(timezone);

// ----------------------------------------------------------------------

export type DatePickerFormat = Dayjs | Date | string | number | null | undefined;

export const formatPatterns = {
  dateTime: 'DD MMM YYYY, h:mm A EST', // 17 Apr 2022 12:00 AM EST
  date: 'DD MMM YYYY', // 17 Apr 2022
  time: 'h:mm a', // 12:00 am
  estTime: 'h:mm A [EST]', // 12:00 AM EST
  split: {
    dateTime: 'DD/MM/YYYY h:mm a', // 17/04/2022 12:00 am
    date: 'DD/MM/YYYY', // 17/04/2022
  },
  paramCase: {
    dateTime: 'DD-MM-YYYY h:mm a', // 17-04-2022 12:00 am
    date: 'DD-MM-YYYY', // 17-04-2022
  },
  iso: {
    dateTime: 'YYYY-MM-DD h:mm a', // 2022-04-17 12:00 am
    date: 'YYYY-MM-DD', // 2022-04-17
  },
  us: {
    dateTime: 'MM/DD/YYYY HH:mm:ss', // 04/17/2022 14:30:45
    date: 'MM/DD/YYYY', // 04/17/2022
  },
};

export const isValidDate = (date: DatePickerFormat) =>
  date !== null && date !== undefined && dayjs(date).isValid();

// ----------------------------------------------------------------------

export function today(template?: string): string {
  return dayjs(new Date()).startOf('day').format(template);
}

// ----------------------------------------------------------------------

/**
 * Checks if a given date is in Eastern Daylight Time (EDT)
 * @param {Dayjs} date - The date to check
 * @returns {boolean} - true if the date is in EDT (UTC−4), false if EST (UTC−5)
 */
export function isEDT(date: Dayjs): boolean {
  const offsetMinutes = date.utcOffset();
  return offsetMinutes === -240; // UTC−4 → EDT
}

// ----------------------------------------------------------------------

/**
 * Formats a date into a date-time string with timezone handling.
 * - Converts UTC dates to the application's default timezone (e.g., 'America/New_York').
 * - Can apply a manual offset in seconds if provided.
 * - Optionally uses the user's system timezone.
 * @param date - The date to format.
 * @param template - Optional custom format string. Defaults to 'DD MMM YYYY, h:mm A EST'.
 * @param offsetSeconds - Optional offset in seconds to apply to the date.
 * @param useSystemTimezone - If true, formats the date using the system's local timezone.
 * @returns The formatted date-time string (e.g., "17 Apr 2022, 12:00 AM EST") or "Invalid date".
 * @output 17 Apr 2022 12:00 am
 */
export function fDateTime(
  date: DatePickerFormat,
  template?: string,
  offsetSeconds?: number | string,
  useSystemTimezone: boolean = false
): string {
  if (!isValidDate(date)) {
    return 'Invalid date';
  }

  if (useSystemTimezone) {
    return dayjsSystem(date).format(template ?? formatPatterns.dateTime);
  }

  // If offset is provided, assume date is already in target timezone (or handled by wrapper)
  // and apply the offset.
  if (offsetSeconds !== undefined && offsetSeconds !== null) {
    let dayjsDate = dayjs(date);
    const offset = typeof offsetSeconds === 'string' ? parseInt(offsetSeconds, 10) : offsetSeconds;
    if (!Number.isNaN(offset)) {
      dayjsDate = dayjsDate.add(offset, 'second');
    }
    return dayjsDate.format(template ?? formatPatterns.dateTime);
  }

  // If no offset is provided, assume date is in UTC and convert to default timezone (EST).
  return dayjs
    .utc(date)
    .tz()
    .format(template ?? formatPatterns.dateTime);
}

// ----------------------------------------------------------------------

/**
 * Formats a date into a date-only string.
 * - Assumes the date is in the desired timezone and does not perform any conversion.
 * - Optionally uses the user's system timezone for formatting.
 * @param date - The date to format.
 * @param template - Optional custom format string. Defaults to 'DD MMM YYYY'.
 * @param useSystemTimezone - If true, formats the date using the system's local timezone.
 * @returns The formatted date string (e.g., "17 Apr 2022") or "Invalid date".
 * @output 17 Apr 2022
 */
export function fDate(
  date: DatePickerFormat,
  template?: string,
  useSystemTimezone: boolean = false
): string {
  if (!isValidDate(date)) {
    return 'Invalid date';
  }

  if (useSystemTimezone) {
    return dayjsSystem(date).format(template ?? formatPatterns.date);
  }

  return dayjs(date).format(template ?? formatPatterns.date);
}

// ----------------------------------------------------------------------

/**
 * Formats a date into a time-only string.
 * - Assumes the date is in the desired timezone and does not perform any conversion.
 * - Optionally uses the user's system timezone for formatting.
 * @param date - The date to format.
 * @param template - Optional custom format string. Defaults to 'h:mm a'.
 * @param useSystemTimezone - If true, formats the date using the system's local timezone.
 * @returns The formatted time string (e.g., "5:00 am") or "Invalid date".
 * @output 5:00 am
 */
export function fTime(
  date: DatePickerFormat,
  template?: string,
  useSystemTimezone: boolean = false
): string {
  if (!isValidDate(date)) {
    return 'Invalid date';
  }

  if (useSystemTimezone) {
    return dayjsSystem(date).format(template ?? formatPatterns.time);
  }

  return dayjs(date).format(template ?? formatPatterns.time);
}

// ----------------------------------------------------------------------

/**
 * @output 1713250100
 */
export function fTimestamp(date: DatePickerFormat): number | 'Invalid date' {
  if (!isValidDate(date)) {
    return 'Invalid date';
  }

  return dayjs(date).valueOf();
}

// ----------------------------------------------------------------------

/**
 * @output a few seconds, 2 years
 */
export function fToNow(date: DatePickerFormat): string {
  if (!isValidDate(date)) {
    return 'Invalid date';
  }

  return dayjs(date).toNow(true);
}

// ----------------------------------------------------------------------

/**
 * @output boolean
 */
export function fIsBetween(
  inputDate: DatePickerFormat,
  startDate: DatePickerFormat,
  endDate: DatePickerFormat
): boolean {
  if (!isValidDate(inputDate) || !isValidDate(startDate) || !isValidDate(endDate)) {
    return false;
  }

  const formattedInputDate = fTimestamp(inputDate);
  const formattedStartDate = fTimestamp(startDate);
  const formattedEndDate = fTimestamp(endDate);

  if (
    formattedInputDate === 'Invalid date' ||
    formattedStartDate === 'Invalid date' ||
    formattedEndDate === 'Invalid date'
  ) {
    return false;
  }

  return formattedInputDate >= formattedStartDate && formattedInputDate <= formattedEndDate;
}

// ----------------------------------------------------------------------

/**
 * @output boolean
 */
export function fIsAfter(startDate: DatePickerFormat, endDate: DatePickerFormat): boolean {
  if (!isValidDate(startDate) || !isValidDate(endDate)) {
    return false;
  }

  return dayjs(startDate).isAfter(endDate);
}

// ----------------------------------------------------------------------

/**
 * @output boolean
 */
export function fIsSame(
  startDate: DatePickerFormat,
  endDate: DatePickerFormat,
  unitToCompare?: OpUnitType
): boolean {
  if (!isValidDate(startDate) || !isValidDate(endDate)) {
    return false;
  }

  return dayjs(startDate).isSame(endDate, unitToCompare ?? 'year');
}

/**
 * @output
 * Same day: 26 Apr 2024
 * Same month: 25 - 26 Apr 2024
 * Same month: 25 - 26 Apr 2024
 * Same year: 25 Apr - 26 May 2024
 */
export function fDateRangeShortLabel(
  startDate: DatePickerFormat,
  endDate: DatePickerFormat,
  initial?: boolean
): string {
  if (!isValidDate(startDate) || !isValidDate(endDate) || fIsAfter(startDate, endDate)) {
    return 'Invalid date';
  }

  let label = `${fDate(startDate)} - ${fDate(endDate)}`;

  if (initial) {
    return label;
  }

  const isSameYear = fIsSame(startDate, endDate, 'year');
  const isSameMonth = fIsSame(startDate, endDate, 'month');
  const isSameDay = fIsSame(startDate, endDate, 'day');

  if (isSameYear && !isSameMonth) {
    label = `${fDate(startDate, 'DD MMM')} - ${fDate(endDate)}`;
  } else if (isSameYear && isSameMonth && !isSameDay) {
    label = `${fDate(startDate, 'DD')} - ${fDate(endDate)}`;
  } else if (isSameYear && isSameMonth && isSameDay) {
    label = `${fDate(endDate)}`;
  }

  return label;
}

// ----------------------------------------------------------------------

/**
 * @output 2024-05-28T05:55:31+00:00
 */
export type DurationProps = {
  years?: number;
  months?: number;
  days?: number;
  hours?: number;
  minutes?: number;
  seconds?: number;
  milliseconds?: number;
};

export function fAdd({
  years = 0,
  months = 0,
  days = 0,
  hours = 0,
  minutes = 0,
  seconds = 0,
  milliseconds = 0,
}: DurationProps) {
  const result = dayjs()
    .add(
      dayjs.duration({
        years,
        months,
        days,
        hours,
        minutes,
        seconds,
        milliseconds,
      })
    )
    .format();

  return result;
}

/**
 * @output 2024-05-28T05:55:31+00:00
 */
export function fSub({
  years = 0,
  months = 0,
  days = 0,
  hours = 0,
  minutes = 0,
  seconds = 0,
  milliseconds = 0,
}: DurationProps) {
  const result = dayjs()
    .subtract(
      dayjs.duration({
        years,
        months,
        days,
        hours,
        minutes,
        seconds,
        milliseconds,
      })
    )
    .format();

  return result;
}

export function formatDuration(inputDuration: string | number | null | undefined): string {
  // Convert input to number
  const totalSeconds = Number(inputDuration);

  // Check if the result is NaN or negative. This handles null, undefined, '', 'N/A', etc.
  if (Number.isNaN(totalSeconds) || totalSeconds < 0) {
    return 'N/A';
  }

  // Calculate minutes and seconds
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;

  // Format with padding
  const formattedMinutes = String(minutes).padStart(2, '0');
  const formattedSeconds = String(seconds).padStart(2, '0');

  return `${formattedMinutes}:${formattedSeconds}`;
}

/**
 * Returns a human-readable description for a date range.
 * - If the range is yesterday, it returns "Yesterday".
 * - If the range is this week, it returns "This week".
 * - Otherwise, it returns the formatted date range "MMM D, YYYY - MMM D, YYYY".
 * @param newStartDate - The start date of the range in 'YYYY-MM-DD' format.
 * @param newEndDate - The end date of the range in 'YYYY-MM-DD' format.
 * @returns A string describing the date range.
 */
export function formatDateDescription(newStartDate: string, newEndDate: string): string {
  const todayDate = dayjs();
  const yesterday = todayDate.subtract(1, 'day');
  const startOfWeek = todayDate.startOf('week');
  const endOfWeek = todayDate.endOf('week');

  const startDate = dayjs(newStartDate, 'YYYY-MM-DD');
  const endDate = dayjs(newEndDate, 'YYYY-MM-DD');

  if (startDate.isSame(yesterday, 'day') && endDate.isSame(yesterday, 'day')) {
    return 'Yesterday';
  }

  if (startDate.isSame(startOfWeek, 'day') && endDate.isSame(endOfWeek, 'day')) {
    return 'This week';
  }

  return `${startDate.format('MMM D, YYYY')} - ${endDate.format('MMM D, YYYY')}`;
}

/**
 * Converts a date string (e.g., 'YYYY-MM-DD') to its UTC ISO string representation.
 * assuming the date is in the 'America/New_York' (EST/EDT) timezone.
 * For 'start', it's the beginning of the given day.
 * For 'end', it's the beginning of the next day.
 * This is based on the user's local timezone.
 * @param date - The date to convert.
 * @param boundary - 'start' for the beginning of the day, 'end' for the beginning of the next day. Defaults to 'start'.
 * @example
 * // If data is in EST (UTC-5)
 * fDateToUTC('2025-11-11', 'start') // returns '2025-11-11T05:00:00Z'
 * fDateToUTC('2025-11-11', 'end')   // returns '2025-11-12T05:00:00Z'
 * @returns The UTC ISO string or null if the date is invalid.
 */
export function fDateToUTC(
  date: DatePickerFormat,
  boundary: 'start' | 'end' = 'start'
): string | null {
  if (!isValidDate(date)) {
    return null;
  }

  const currentTime = dayjs(date);

  // Always adjust for Daylight Saving Time
  const dayjsDate = isEDT(currentTime) ? currentTime : currentTime.add(1, 'hour');

  if (boundary === 'end') {
    return dayjsDate.add(1, 'day').startOf('day').utc().format();
  }

  return dayjsDate.startOf('day').utc().format();
}
